'use strict';
import React, { useMemo, useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import CloseIcon from '@mui/icons-material/Close';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined';
import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	Grid,
	IconButton,
	Stack,
	Switch,
	Tab,
	Tabs,
	TextField,
	Typography,
} from '@mui/material';
import { formatTalentLmsBranchSummary } from './talentLmsSync';
import {
	isEmployeeEmail,
	isSalesforceApprovalManagedAutomatically,
	isSalesforceApprovedFromStatus,
	SALESFORCE_APPROVAL_MANAGED_AUTOMATICALLY_MESSAGE,
	showSalesforceApprovalControl,
} from '../../../utils/salesforceApprovalRules';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const TAB_BASIC = 'basic';
const TAB_SALESFORCE = 'salesforce';
const TAB_TALENTLMS = 'talentlms';

/** Fixed tab panel height — keeps dialog size stable across tab switches. */
const TAB_PANEL_HEIGHT = 540;
/** Reserved space below tab panel so save errors do not resize the dialog. */
const ERROR_ALERT_SLOT_MIN_HEIGHT = 56;

const HEADER_SX = {
	bgcolor: '#061B3A',
	color: 'common.white',
	px: { xs: 3, sm: 5 },
	py: 3,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	boxShadow: '0 2px 8px rgba(6, 27, 58, 0.18)',
	position: 'relative',
	zIndex: 1,
};

const CLOSE_BUTTON_SX = {
	color: 'common.white',
	border: '1px solid',
	borderColor: 'rgba(255, 255, 255, 0.25)',
	borderRadius: 1.5,
	p: 0.75,
	'&:hover': {
		bgcolor: 'rgba(255, 255, 255, 0.12)',
		borderColor: 'rgba(255, 255, 255, 0.4)',
	},
};

const TABS_SX = {
	minHeight: 'auto',
	'& .MuiTabs-flexContainer': {
		gap: 3,
	},
	'& .MuiTab-root': {
		minWidth: 'auto',
		minHeight: 'auto',
		px: 0,
		pb: 1.5,
		textTransform: 'none',
		fontSize: '0.9375rem',
		fontWeight: 600,
		color: 'text.secondary',
		'&.Mui-selected': {
			color: 'primary.main',
		},
	},
	'& .MuiTabs-indicator': {
		height: 3,
		borderRadius: 3,
		backgroundColor: 'primary.main',
	},
};

const TAB_SETTINGS_CARD_SX = {
	maxWidth: 750,
	width: '100%',
	borderRadius: 2,
	border: 1,
	borderColor: 'divider',
	boxShadow: 1,
};

const TAB_SETTINGS_CARD_WRAPPER_SX = {
	display: 'flex',
	justifyContent: 'center',
	width: '100%',
};

const getSalesforceStatusChipProps = (status) => {
	const normalized = status || 'None';

	if (normalized === 'None') {
		return { label: 'None', color: 'error', variant: 'outlined' };
	}

	if (normalized === 'Lead') {
		return { label: 'Pending', color: 'warning', variant: 'filled' };
	}

	if (normalized === 'Rejected Lead') {
		return { label: 'Rejected', color: 'error', variant: 'filled' };
	}

	return { label: normalized, color: 'success', variant: 'filled' };
};

const getTalentLmsSyncChipProps = (syncStatus, isReconciling) => {
	if (isReconciling) {
		return { label: 'Sync Pending', color: 'info', variant: 'filled' };
	}

	switch (syncStatus) {
		case 'Succeeded':
			return { label: 'Synced', color: 'success', variant: 'filled' };
		case 'Failed':
			return { label: 'Sync Failed', color: 'error', variant: 'filled' };
		case 'Skipped':
			return { label: 'Sync Pending', color: 'info', variant: 'filled' };
		case 'NeverSynced':
		default:
			return { label: 'Never Synced', color: 'warning', variant: 'filled' };
	}
};

const getTalentLmsBranchChipProps = (branchCount) => {
	if (branchCount <= 0) {
		return { label: 'None Assigned', color: 'default', variant: 'outlined' };
	}

	return {
		label: branchCount === 1 ? '1 Branch' : `${branchCount} Branches`,
		color: 'primary',
		variant: 'filled',
	};
};

const formatTalentLmsLastSync = (utc) => {
	if (!utc) return 'Never';

	const date = new Date(utc);
	if (Number.isNaN(date.getTime())) return utc;

	return date.toLocaleString(undefined, {
		dateStyle: 'medium',
		timeStyle: 'short',
	});
};

const getTalentLmsLastResultDisplay = (syncStatus, lastSyncError) => {
	if (lastSyncError || syncStatus === 'Failed') {
		return { type: 'chip', label: 'Failed', color: 'error' };
	}

	if (syncStatus === 'Succeeded') {
		return { type: 'chip', label: 'Successful', color: 'success' };
	}

	return { type: 'text', label: 'N/A' };
};

const TEXT_FIELD_SX = {
	'& .MuiOutlinedInput-root': {
		height: 'auto',
		minHeight: 56,
		padding: 0,
		alignItems: 'center',
	},
	'& .MuiOutlinedInput-input': {
		py: 1.5,
		px: 1.75,
		height: 'auto',
		boxSizing: 'border-box',
	},
	'& .MuiInputLabel-outlined': {
		backgroundColor: 'background.paper',
		px: 0.5,
		zIndex: 1,
	},
};

export const EditUserModal = ({
	open,
	onCancel,
	onSubmit,
	isSubmitting,
	errorMessage,
	initialValues,
	canViewAdmin,
	loginStatus,
	onRetryLoginStatus,
	onToggleApproval,
	isApproving,
	talentLmsSync,
	onRetryTalentLmsSync,
	onReconcileTalentLms,
	isReconciling,
}) => {
	const [activeTab, setActiveTab] = useState(TAB_BASIC);
	const [formValues, setFormValues] = useState({
		firstName: '',
		lastName: '',
		email: '',
	});
	const [touched, setTouched] = useState({
		firstName: false,
		lastName: false,
		email: false,
	});

	const resetForm = useCallback(() => {
		setFormValues({
			firstName: '',
			lastName: '',
			email: '',
		});
		setTouched({
			firstName: false,
			lastName: false,
			email: false,
		});
		setActiveTab(TAB_BASIC);
	}, []);

	const initialFirstName = initialValues?.firstName || '';
	const initialLastName = initialValues?.lastName || '';
	const initialEmail = initialValues?.email || '';

	useEffect(() => {
		if (open) {
			setFormValues({
				firstName: initialFirstName,
				lastName: initialLastName,
				email: initialEmail,
			});
			setTouched({
				firstName: false,
				lastName: false,
				email: false,
			});
			setActiveTab(TAB_BASIC);
		}
		// Reinitialize only when the dialog opens or the target user changes,
		// not on unrelated parent re-renders (e.g. Salesforce/TalentLMS retries).
	}, [open, initialFirstName, initialLastName, initialEmail]);

	const showSalesforceTab = canViewAdmin;

	const tabs = useMemo(() => {
		const result = [{ key: TAB_BASIC, label: 'Basic' }];
		if (showSalesforceTab) {
			result.push({ key: TAB_SALESFORCE, label: 'Salesforce' });
		}
		if (canViewAdmin) {
			result.push({ key: TAB_TALENTLMS, label: 'TalentLMS' });
		}
		return result;
	}, [canViewAdmin, showSalesforceTab]);

	useEffect(() => {
		if (!tabs.some((tab) => tab.key === activeTab)) {
			setActiveTab(TAB_BASIC);
		}
	}, [tabs, activeTab]);

	const handleChange = (field) => (event) => {
		setFormValues((prev) => ({
			...prev,
			[field]: event.target.value,
		}));
		setTouched((prev) => ({
			...prev,
			[field]: true,
		}));
	};

	const handleBlur = (field) => () => {
		setTouched((prev) => ({
			...prev,
			[field]: true,
		}));
	};

	const errors = useMemo(() => {
		const firstName = formValues.firstName.trim();
		const lastName = formValues.lastName.trim();
		const email = formValues.email.trim();
		const isEmailValid = email.length > 0 && EMAIL_REGEX.test(email);

		return {
			firstName: firstName.length === 0 ? 'First name is required' : '',
			lastName: lastName.length === 0 ? 'Last name is required' : '',
			email: email.length === 0
				? 'Email is required'
				: isEmailValid
					? ''
					: 'Enter a valid email address',
		};
	}, [formValues]);

	const isValid = useMemo(
		() => Object.values(errors).every((error) => !error),
		[errors],
	);

	const handleCancel = () => {
		resetForm();
		onCancel();
	};

	const handleSubmit = () => {
		if (!isValid || isSubmitting) return;
		onSubmit({
			firstName: formValues.firstName.trim(),
			lastName: formValues.lastName.trim(),
			email: formValues.email.trim(),
		});
	};

	const branchSummary = formatTalentLmsBranchSummary(talentLmsSync?.branches || []);
	const activeTabIndex = Math.max(0, tabs.findIndex((tab) => tab.key === activeTab));

	const renderBasicTab = () => (
		<Box sx={TAB_SETTINGS_CARD_WRAPPER_SX}>
			<Card sx={TAB_SETTINGS_CARD_SX} elevation={1}>
				<CardContent sx={{ p: '24px' }}>
					<Stack spacing={3}>
						<Stack direction="row" spacing={2} alignItems="flex-start">
							<AccountCircleOutlinedIcon
								color="primary"
								sx={{ fontSize: 28, mt: 0.25, flexShrink: 0 }}
							/>
							<Box sx={{ minWidth: 0 }}>
								<Typography variant="subtitle1" fontWeight={700}>
									Basic Information
								</Typography>
								<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
									Manage this user&apos;s name and email address.
								</Typography>
							</Box>
						</Stack>

						<Divider />

						<Grid container spacing={3}>
							<Grid item xs={12} sm={6}>
								<TextField
									required
									label="First Name"
									value={formValues.firstName}
									onChange={handleChange('firstName')}
									onBlur={handleBlur('firstName')}
									name="firstName"
									autoComplete="given-name"
									variant="outlined"
									fullWidth
									sx={TEXT_FIELD_SX}
									InputLabelProps={{ shrink: !!formValues.firstName }}
									error={touched.firstName && !!errors.firstName}
									helperText={touched.firstName && errors.firstName ? errors.firstName : ' '}
								/>
							</Grid>
							<Grid item xs={12} sm={6}>
								<TextField
									required
									label="Last Name"
									value={formValues.lastName}
									onChange={handleChange('lastName')}
									onBlur={handleBlur('lastName')}
									name="lastName"
									autoComplete="family-name"
									variant="outlined"
									fullWidth
									sx={TEXT_FIELD_SX}
									InputLabelProps={{ shrink: !!formValues.lastName }}
									error={touched.lastName && !!errors.lastName}
									helperText={touched.lastName && errors.lastName ? errors.lastName : ' '}
								/>
							</Grid>
							<Grid item xs={12}>
								<TextField
									required
									label="Email"
									value={formValues.email}
									onChange={handleChange('email')}
									onBlur={handleBlur('email')}
									name="email"
									type="email"
									autoComplete="email"
									variant="outlined"
									fullWidth
									sx={TEXT_FIELD_SX}
									InputLabelProps={{ shrink: !!formValues.email }}
									error={touched.email && !!errors.email}
									helperText={touched.email && errors.email ? errors.email : ' '}
								/>
							</Grid>
						</Grid>
					</Stack>
				</CardContent>
			</Card>
		</Box>
	);

	const renderLoadingState = (message) => (
		<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
			<CircularProgress size={20} />
			<Typography variant="body2">{message}</Typography>
		</Box>
	);

	const renderSalesforceTab = () => {
		const userEmail = formValues.email || initialEmail;
		const resolvedStatus = loginStatus?.status
			|| (isEmployeeEmail(userEmail) ? 'Employee' : '');
		const isManagedAutomatically = isSalesforceApprovalManagedAutomatically(
			userEmail,
			null,
			resolvedStatus,
		);
		const isApproved = isSalesforceApprovedFromStatus(resolvedStatus);
		const showApprovalToggle = showSalesforceApprovalControl(resolvedStatus);
		const statusChipProps = getSalesforceStatusChipProps(resolvedStatus);

		const renderCardBody = () => {
			if (loginStatus?.loading) {
				return renderLoadingState('Loading login status...');
			}

			if (loginStatus?.error && !resolvedStatus) {
				return (
					<Stack spacing={2}>
						<Alert severity="error">Failed to load login status.</Alert>
						<Box>
							<Button size="small" variant="outlined" onClick={onRetryLoginStatus}>
								Retry
							</Button>
						</Box>
					</Stack>
				);
			}

			return (
				<Stack spacing={3}>
					<Box>
						<Typography
							variant="overline"
							color="text.secondary"
							sx={{ display: 'block', mb: 2, letterSpacing: 1.1 }}
						>
							Salesforce Access
						</Typography>

						<Stack
							direction="row"
							alignItems="center"
							justifyContent="space-between"
							spacing={2}
						>
							<Typography variant="body2" fontWeight={600}>
								Login Status
							</Typography>
							<Chip
								size="small"
								label={statusChipProps.label}
								color={statusChipProps.color}
								variant={statusChipProps.variant}
							/>
						</Stack>
					</Box>

					<Divider />

					<Stack
						direction="row"
						alignItems="center"
						justifyContent="space-between"
						spacing={2}
					>
						<Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
							<ShieldOutlinedIcon color="action" sx={{ fontSize: 28, flexShrink: 0 }} />
							<Box sx={{ minWidth: 0 }}>
								<Typography fontWeight={600}>
									Approved
								</Typography>
								{isManagedAutomatically ? (
									<Typography variant="body2" color="text.secondary">
										{SALESFORCE_APPROVAL_MANAGED_AUTOMATICALLY_MESSAGE}
									</Typography>
								) : (
									<Typography variant="body2" color="text.secondary">
										Allow this user to access Salesforce features.
									</Typography>
								)}
							</Box>
						</Stack>
						{showApprovalToggle ? (
							<Switch
								checked={isApproved}
								disabled={isApproved || isApproving}
								onChange={onToggleApproval}
								inputProps={{ 'aria-label': 'Salesforce approved' }}
							/>
						) : null}
					</Stack>
				</Stack>
			);
		};

		return (
			<Box sx={TAB_SETTINGS_CARD_WRAPPER_SX}>
				<Card sx={TAB_SETTINGS_CARD_SX} elevation={1}>
					<CardContent sx={{ p: '24px' }}>
						{renderCardBody()}
					</CardContent>
				</Card>
			</Box>
		);
	};

	const renderTalentLmsTab = () => {
		const branchCount = branchSummary.length;
		const branchChipProps = getTalentLmsBranchChipProps(branchCount);
		const syncStatus = talentLmsSync?.syncStatus || 'NeverSynced';
		const syncChipProps = getTalentLmsSyncChipProps(syncStatus, isReconciling);
		const lastResultDisplay = getTalentLmsLastResultDisplay(
			syncStatus,
			talentLmsSync?.lastSyncError,
		);

		const renderSettingsRow = (label, control) => (
			<Stack
				direction="row"
				alignItems="center"
				justifyContent="space-between"
				spacing={2}
			>
				<Typography variant="body2" fontWeight={600}>
					{label}
				</Typography>
				<Box sx={{ flexShrink: 0 }}>{control}</Box>
			</Stack>
		);

		const renderCardBody = () => {
			if (talentLmsSync?.loading) {
				return renderLoadingState('Loading TalentLMS sync status...');
			}

			if (talentLmsSync?.error) {
				return (
					<Stack spacing={2}>
						<Alert severity="error">Failed to load TalentLMS sync status.</Alert>
						<Box>
							<Button size="small" variant="outlined" onClick={onRetryTalentLmsSync}>
								Retry
							</Button>
						</Box>
					</Stack>
				);
			}

			return (
				<Stack spacing={3}>
					<Stack direction="row" spacing={2} alignItems="flex-start">
						<SchoolOutlinedIcon color="primary" sx={{ fontSize: 28, mt: 0.25, flexShrink: 0 }} />
						<Box sx={{ minWidth: 0 }}>
							<Typography variant="subtitle1" fontWeight={700}>
								TalentLMS Integration
							</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
								Manage this user&apos;s TalentLMS branch assignment and synchronization status.
							</Typography>
						</Box>
					</Stack>

					<Divider />

					{renderSettingsRow(
						'Learning Center Branches',
						<Chip
							size="small"
							icon={<AccountTreeOutlinedIcon />}
							label={branchChipProps.label}
							color={branchChipProps.color}
							variant={branchChipProps.variant}
						/>,
					)}

					<Divider />

					{renderSettingsRow(
						'TalentLMS Sync',
						<Chip
							size="small"
							label={syncChipProps.label}
							color={syncChipProps.color}
							variant={syncChipProps.variant}
						/>,
					)}

					<Divider />

					<Stack spacing={2}>
						{renderSettingsRow(
							'Last Successful Sync',
							<Typography variant="body2" color="text.secondary">
								{formatTalentLmsLastSync(talentLmsSync?.lastSyncSuccessUtc)}
							</Typography>,
						)}
						{renderSettingsRow(
							'Last Attempt',
							<Typography variant="body2" color="text.secondary">
								{formatTalentLmsLastSync(talentLmsSync?.lastSyncAttemptUtc)}
							</Typography>,
						)}
						{renderSettingsRow(
							'Last Result',
							lastResultDisplay.type === 'chip' ? (
								<Chip
									size="small"
									label={lastResultDisplay.label}
									color={lastResultDisplay.color}
									variant="filled"
								/>
							) : (
								<Typography variant="body2" color="text.secondary">
									{lastResultDisplay.label}
								</Typography>
							),
						)}
					</Stack>

					{talentLmsSync?.lastSyncError ? (
						<Alert severity="error" sx={{ '& .MuiAlert-message': { whiteSpace: 'pre-line' } }}>
							{talentLmsSync.lastSyncError}
						</Alert>
					) : null}

					<Divider />

					<Stack
						direction={{ xs: 'column', sm: 'row' }}
						alignItems={{ xs: 'stretch', sm: 'center' }}
						justifyContent="space-between"
						spacing={2}
					>
						<Stack direction="row" spacing={2} alignItems="flex-start" sx={{ flex: 1, minWidth: 0 }}>
							<SyncOutlinedIcon color="action" sx={{ fontSize: 28, mt: 0.25, flexShrink: 0 }} />
							<Box sx={{ minWidth: 0 }}>
								<Typography fontWeight={600}>
									Sync with TalentLMS
								</Typography>
								<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
									Synchronize this user&apos;s profile, branch assignments, enrollment data,
									and account status with TalentLMS.
								</Typography>
							</Box>
						</Stack>
						<Button
							variant="contained"
							color="primary"
							disabled={isReconciling}
							onClick={onReconcileTalentLms}
							startIcon={
								isReconciling
									? <CircularProgress size={16} color="inherit" />
									: <SyncOutlinedIcon />
							}
							sx={{ alignSelf: { xs: 'stretch', sm: 'center' }, flexShrink: 0, minWidth: 160 }}
						>
							Sync TalentLMS
						</Button>
					</Stack>
				</Stack>
			);
		};

		return (
			<Box sx={TAB_SETTINGS_CARD_WRAPPER_SX}>
				<Card sx={TAB_SETTINGS_CARD_SX} elevation={1}>
					<CardContent sx={{ p: '24px' }}>
						{renderCardBody()}
					</CardContent>
				</Card>
			</Box>
		);
	};

	const renderActiveTabPanel = () => {
		switch (activeTab) {
			case TAB_SALESFORCE:
				return renderSalesforceTab();
			case TAB_TALENTLMS:
				return renderTalentLmsTab();
			default:
				return renderBasicTab();
		}
	};

	return (
		<Dialog
			open={open}
			onClose={isSubmitting ? undefined : handleCancel}
			maxWidth="md"
			fullWidth
			PaperProps={{
				sx: {
					borderRadius: 2,
					overflow: 'hidden',
					display: 'flex',
					flexDirection: 'column',
				},
			}}
		>
			<DialogTitle component="div" sx={HEADER_SX}>
				<Typography component="h2" sx={{ fontSize: { xs: 22, sm: 26 }, fontWeight: 700, lineHeight: 1.2 }}>
					Edit User
				</Typography>
				<IconButton
					aria-label="Close edit user dialog"
					onClick={handleCancel}
					disabled={isSubmitting}
					sx={CLOSE_BUTTON_SX}
				>
					<CloseIcon />
				</IconButton>
			</DialogTitle>

			<>
				<DialogContent
					sx={{
						px: { xs: 3, sm: 5 },
						pb: { xs: 3, sm: 4 },
						display: 'flex',
						flexDirection: 'column',
						flex: '1 1 auto',
						overflow: 'hidden',
						'&.MuiDialogContent-root': {
							pt: { xs: 4, sm: 6 },
						},
					}}
				>
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'flex-end',
							borderBottom: tabs.length > 1 ? 1 : 0,
							borderColor: 'divider',
							mb: 4,
							minHeight: 40,
							flexShrink: 0,
						}}
					>
						{tabs.length > 1 ? (
							<Tabs
								value={activeTabIndex}
								onChange={(_, newIndex) => {
									if (isSubmitting) return;
									setActiveTab(tabs[newIndex]?.key || TAB_BASIC);
								}}
								sx={TABS_SX}
							>
								{tabs.map((tab) => (
									<Tab key={tab.key} label={tab.label} disableRipple disabled={isSubmitting} />
								))}
							</Tabs>
						) : null}
					</Box>

					<Box
						sx={{
							height: TAB_PANEL_HEIGHT,
							minHeight: TAB_PANEL_HEIGHT,
							maxHeight: TAB_PANEL_HEIGHT,
							overflowY: 'auto',
							// Theme lifts shrunk outlined labels ~16.5px; pad so top-row labels are not clipped.
							pt: 5,
							boxSizing: 'border-box',
							position: 'relative',
							flexShrink: 0,
						}}
					>
						{renderActiveTabPanel()}
						{isSubmitting ? (
							<Box
								sx={{
									position: 'absolute',
									inset: 0,
									display: 'flex',
									flexDirection: 'column',
									alignItems: 'center',
									justifyContent: 'center',
									gap: 2,
									bgcolor: 'background.paper',
									opacity: 0.92,
									zIndex: 2,
								}}
							>
								<CircularProgress size={32} />
								<Typography variant="body2">Saving user…</Typography>
							</Box>
						) : null}
					</Box>

					<Box
						sx={{
							mt: 3,
							minHeight: ERROR_ALERT_SLOT_MIN_HEIGHT,
							flexShrink: 0,
						}}
					>
						{errorMessage ? (
							<Alert severity="error">
								{errorMessage}
							</Alert>
						) : null}
					</Box>
				</DialogContent>

				<DialogActions
					sx={{
						px: { xs: 3, sm: 5 },
						py: 2.5,
						borderTop: 1,
						borderColor: 'divider',
						gap: 1.5,
					}}
				>
					<Button
						variant="text"
						color="primary"
						onClick={handleCancel}
						disabled={isSubmitting}
						sx={{ fontWeight: 600 }}
					>
						Cancel
					</Button>
					<Button
						variant="contained"
						color="primary"
						onClick={handleSubmit}
						disabled={!isValid || isSubmitting}
						sx={{ fontWeight: 600, px: 3, boxShadow: 'none', '&:hover': { boxShadow: 'none' } }}
					>
						{isSubmitting ? 'Saving…' : 'Save User'}
					</Button>
				</DialogActions>
			</>
		</Dialog>
	);
};

EditUserModal.propTypes = {
	open: PropTypes.bool,
	onCancel: PropTypes.func.isRequired,
	onSubmit: PropTypes.func.isRequired,
	isSubmitting: PropTypes.bool,
	errorMessage: PropTypes.string,
	initialValues: PropTypes.shape({
		firstName: PropTypes.string,
		lastName: PropTypes.string,
		email: PropTypes.string,
	}),
	canViewAdmin: PropTypes.bool,
	loginStatus: PropTypes.shape({
		loading: PropTypes.bool,
		error: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
		status: PropTypes.string,
	}),
	onRetryLoginStatus: PropTypes.func,
	onToggleApproval: PropTypes.func,
	isApproving: PropTypes.bool,
	talentLmsSync: PropTypes.shape({
		loading: PropTypes.bool,
		error: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
		syncStatus: PropTypes.string,
		branches: PropTypes.array,
		lastSyncSuccessUtc: PropTypes.string,
		lastSyncAttemptUtc: PropTypes.string,
		lastSyncError: PropTypes.string,
	}),
	onRetryTalentLmsSync: PropTypes.func,
	onReconcileTalentLms: PropTypes.func,
	isReconciling: PropTypes.bool,
};

EditUserModal.defaultProps = {
	open: false,
	isSubmitting: false,
	errorMessage: '',
	initialValues: null,
	canViewAdmin: false,
	loginStatus: null,
	onRetryLoginStatus: undefined,
	onToggleApproval: undefined,
	isApproving: false,
	talentLmsSync: null,
	onRetryTalentLmsSync: undefined,
	onReconcileTalentLms: undefined,
	isReconciling: false,
};

export default EditUserModal;
